/**
 * Searchspring Beacon API v2
 * @see https://docs.searchspring.com/reference/beacon-tracking
 */
(function () {
  'use strict';

  const INITIATOR = 'ksubi/shopify-theme/1.0';
  const STORAGE_USER = 'ss_beacon_userId';
  const STORAGE_SESSION = 'ss_beacon_sessionId';
  const STORAGE_SESSION_TS = 'ss_beacon_sessionTs';
  const STORAGE_LOGIN_SENT = 'ss_beacon_loginSent';
  const SESSION_MS = 30 * 60 * 1000;
  const IMPRESSION_THRESHOLD = 0.5;
  const BEACON_ORIGIN = 'https://analytics.searchspring.net/beacon/v2';

  /** @type {any} */
  const config = window.ssBeaconConfig || {};
  const siteId = (config.siteId || window.ssSiteId || '').trim();
  if (!siteId || config.enabled === false) return;

  /** @type {string} */
  let pageLoadId = uuid();
  /** @type {any[] | null} */
  let lastCartSnapshot = null;

  /**
   * @typedef {{ responseId: string, results?: any[], tag?: string, module: string, query?: string }} ApiCacheEntry
   */

  /** @type {{ search: ApiCacheEntry | null, category: ApiCacheEntry | null, autocomplete: ApiCacheEntry | null, recommendations: Record<string, ApiCacheEntry> }} */
  const apiCache = {
    search: null,
    category: null,
    autocomplete: null,
    recommendations: {},
  };

  /** @type {Set<string>} */
  const renderedRecommendationTags = new Set();
  /** @type {Map<string, Set<string>>} */
  const impressedKeys = new Map();
  /** @type {WeakMap<HTMLElement, IntersectionObserver>} */
  const impressionObservers = new WeakMap();

  const SIDE_CART_TAG = 'side-cart';
  const SIDE_CART_WIDGET_ID = 'ss__recommendation--side-cart';
  const DESKTOP_MIN_WIDTH = 768;
  const RECOMMENDATION_CONTAINER_SELECTOR =
    '[data-product-recommendations-searchspring], .searchspring--product-recs--container, div[searchspring-recommend], #' +
    SIDE_CART_WIDGET_ID;
  const RECOMMENDATION_ROOT_SELECTOR =
    RECOMMENDATION_CONTAINER_SELECTOR +
    ', .ss__searchspring__recommendations, [searchspring-recommend]';

  function isDesktopViewport() {
    return window.innerWidth >= DESKTOP_MIN_WIDTH;
  }

  function uuid() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function nowIso() {
    return new Date().toISOString();
  }

  function readStorage(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  function writeStorage(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* ignore */
    }
  }

  function getUserId() {
    let id = readStorage(STORAGE_USER);
    if (!id) {
      id = uuid();
      writeStorage(STORAGE_USER, id);
    }
    return id;
  }

  function getSessionId() {
    const now = Date.now();
    let sessionId = readStorage(STORAGE_SESSION);
    const ts = Number(readStorage(STORAGE_SESSION_TS) || '0');
    if (!sessionId || !ts || now - ts > SESSION_MS) {
      sessionId = uuid();
      writeStorage(STORAGE_SESSION, sessionId);
    }
    writeStorage(STORAGE_SESSION_TS, String(now));
    return sessionId;
  }

  function touchSession() {
    writeStorage(STORAGE_SESSION_TS, String(Date.now()));
  }

  /**
   * @param {Record<string, unknown>} [extra]
   */
  function buildContext(extra) {
    /** @type {Record<string, unknown>} */
    const context = {
      timestamp: nowIso(),
      pageUrl: window.location.href,
      userId: getUserId(),
      sessionId: getSessionId(),
      pageLoadId,
      initiator: INITIATOR,
    };

    const currency = (config.currency || window.currencyCode || '').trim();
    if (currency) {
      context.currency = { code: currency };
    }

    const shopperId = (config.shopperId || '').trim();
    if (shopperId) {
      context.shopperId = shopperId;
    }

    if (config.dev === true) {
      context.dev = true;
    }

    if (extra) {
      Object.assign(context, extra);
    }

    touchSession();
    return context;
  }

  /**
   * @param {string} module
   * @param {string} event
   * @param {Record<string, unknown> | undefined} data
   */
  function send(module, event, data) {
    const url = `${BEACON_ORIGIN}/${encodeURIComponent(siteId)}/${module}/${event}`;
    /** @type {{ context: Record<string, unknown>, data?: Record<string, unknown> }} */
    const payload = {
      context: buildContext(),
    };
    if (data !== undefined) {
      payload.data = data;
    }

    console.log('[Searchspring Beacon]', {
      module,
      event,
      url,
      ...payload,
    });

    // JSON body with Content-Type: text/plain avoids CORS preflight (OPTIONS).
    // @see https://docs.searchspring.com/reference/FAQ-Beacon-2.0-Migration
    const body = JSON.stringify(payload);

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body,
      keepalive: true,
    }).catch(() => {
      try {
        if (navigator.sendBeacon) {
          const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
          navigator.sendBeacon(url, blob);
        }
      } catch {
        /* ignore */
      }
    });
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  function asId(value) {
    if (value === undefined || value === null) return '';
    return String(value).trim();
  }

  /**
   * @param {any} item
   * @returns {{ parentId: string, uid: string, sku?: string, type?: string } | null}
   */
  function mapApiResult(item) {
    if (!item || typeof item !== 'object') return null;

    const uid = asId(item.uid || item.id || item.variant_id || item.variantId);
    if (!uid) return null;

    const parentId = asId(item.parentId || item.parent_id || item.product_id || item.productId || uid);
    const sku = asId(item.sku || item.SKU);

    /** @type {{ parentId: string, uid: string, sku?: string, type?: string }} */
    const mapped = { parentId, uid, type: item.type || 'product' };
    if (sku) mapped.sku = sku;
    return mapped;
  }

  /**
   * @param {any} item
   * @param {number} [qty]
   * @param {number} [price]
   */
  function mapCartLine(item, qty, price) {
    const productId = asId(item.product_id);
    const variantId = asId(item.variant_id);
    const uid = variantId || productId;
    const parentId = productId || uid;
    if (!uid) return null;

    const line = {
      parentId,
      uid,
      qty: qty ?? item.quantity ?? 1,
      price: price ?? (item.price ? item.price / 100 : 0),
    };

    const sku = asId(item.sku);
    if (sku) line.sku = sku;
    return line;
  }

  /**
   * @param {any[]} items
   */
  function mapCartState(items) {
    return items.map((item) => mapCartLine(item)).filter(Boolean);
  }

  /**
   * @param {string} url
   */
  function isSearchspringApiUrl(url) {
    try {
      const host = new URL(url, window.location.origin).hostname;
      return /searchspring\.(io|net)/i.test(host);
    } catch {
      return false;
    }
  }

  /**
   * @param {string} url
   * @param {string} [requestBody]
   */
  function inferModuleFromUrl(url, requestBody) {
    const lower = url.toLowerCase();
    if (lower.includes('recommend')) return 'recommendations';
    if (lower.includes('autocomplete')) return 'autocomplete';
    if (lower.includes('category')) return 'category';
    if (lower.includes('search')) return 'search';

    if (requestBody) {
      try {
        const parsed = JSON.parse(requestBody);
        if (parsed.tag || parsed.profile) return 'recommendations';
      } catch {
        /* ignore */
      }
    }

    const page = document.querySelector('#plp-app')?.getAttribute('data-page') || '';
    if (page === 'search_page') return 'search';
    if (page === 'collection_page') return 'category';
    return null;
  }

  /**
   * @param {string} url
   */
  function inferRecommendationTag(url) {
    try {
      const u = new URL(url, window.location.origin);
      const tag =
        u.searchParams.get('tag') ||
        u.searchParams.get('profile') ||
        u.searchParams.get('tags');
      if (tag) return tag.split(',')[0].trim();
    } catch {
      /* ignore */
    }
    return '';
  }

  /**
   * @param {string} [requestBody]
   */
  function inferRecommendationTagFromRequest(requestBody) {
    if (!requestBody) return '';

    try {
      const parsed = JSON.parse(requestBody);
      const tags = parsed.tags || parsed.tag;
      if (typeof tags === 'string' && tags.trim()) {
        return tags.split(',')[0].trim();
      }

      if (Array.isArray(parsed.profiles)) {
        const first = parsed.profiles[0];
        if (typeof first === 'string' && first.trim()) return first.trim();
        const profileTag = first?.tag || first?.profile;
        if (typeof profileTag === 'string' && profileTag.trim()) return profileTag.trim();
      }
    } catch {
      /* ignore */
    }

    return '';
  }

  /**
   * Recommendations API returns `profile: { tag: "side-cart" }`, not a string profile id.
   * @param {any} json
   * @param {string} url
   * @param {string} [requestBody]
   */
  function extractRecommendationProfileTag(json, url, requestBody) {
    const fromUrl = inferRecommendationTag(url);
    if (fromUrl) return fromUrl;

    const fromRequest = inferRecommendationTagFromRequest(requestBody);
    if (fromRequest) return fromRequest;

    if (typeof json?.tag === 'string' && json.tag.trim()) return json.tag.trim();
    if (typeof json?.profile === 'string' && json.profile.trim()) return json.profile.trim();
    if (typeof json?.profile?.tag === 'string' && json.profile.tag.trim()) {
      return json.profile.tag.trim();
    }

    return '';
  }

  /**
   * @param {any} json
   */
  function extractResponseId(json) {
    if (!json || typeof json !== 'object') return '';
    const id = json.responseId || json.response_id;
    return id ? String(id).trim() : '';
  }

  /**
   * @param {string} url
   * @param {string} [requestBody]
   */
  function isSideCartRecommendRequest(url, requestBody) {
    if (inferRecommendationTag(url) === SIDE_CART_TAG) return true;
    if (requestBody && requestBody.includes('side-cart')) return true;
    return url.toLowerCase().includes('side-cart');
  }

  /**
   * @param {any} json
   * @param {string} url
   * @param {string} [requestBody]
   */
  function storeRecommendationResponse(json, url, requestBody) {
    const responseId = extractResponseId(json);
    if (!responseId) return;

    const results = Array.isArray(json.results) ? json.results : [];
    const tag = extractRecommendationProfileTag(json, url, requestBody) || 'default';

    /** @type {ApiCacheEntry} */
    const entry = {
      module: 'recommendations',
      tag,
      responseId,
      results,
    };

    apiCache.recommendations[tag] = entry;

    if (tag === SIDE_CART_TAG || isSideCartRecommendRequest(url, requestBody)) {
      apiCache.recommendations[SIDE_CART_TAG] = entry;
    }

    scanRecommendationContainers();
  }

  /**
   * @param {any} json
   * @param {string} module
   * @param {string} url
   * @param {string} [requestBody]
   */
  function storeApiResponse(json, module, url, requestBody) {
    if (module === 'recommendations') {
      storeRecommendationResponse(json, url, requestBody);
      return;
    }

    const responseId = extractResponseId(json);
    if (!responseId) return;

    const results = Array.isArray(json.results)
      ? json.results
      : Array.isArray(json?.results?.[0])
        ? json.results[0]
        : [];

    /** @type {ApiCacheEntry} */
    const entry = {
      module,
      responseId,
      results,
    };

    // Discovery/autocomplete `render` is sent by the search-app (once per page / first dropdown open).
    if (module === 'search') {
      apiCache.search = entry;
    } else if (module === 'category') {
      apiCache.category = entry;
    } else if (module === 'autocomplete') {
      apiCache.autocomplete = entry;
    }
  }

  function ingestSearchspringResponse(url, requestBody, responseText) {
    if (!isSearchspringApiUrl(url) || !responseText) return;

    const module = inferModuleFromUrl(url, requestBody);
    if (!module) return;

    let json;
    try {
      json = JSON.parse(responseText);
    } catch {
      return;
    }

    if (Array.isArray(json)) {
      json.forEach((block) => storeRecommendationResponse(block, url, requestBody));
      return;
    }

    storeApiResponse(json, module, url, requestBody);
  }

  function installXhrInterceptor() {
    const proto = XMLHttpRequest.prototype;
    const origOpen = proto.open;
    const origSend = proto.send;

    proto.open = function patchedXhrOpen(method, url) {
      this.__ssUrl = typeof url === 'string' ? url : String(url);
      return origOpen.apply(this, arguments);
    };

    proto.send = function patchedXhrSend(body) {
      const xhr = this;
      const requestUrl = xhr.__ssUrl || '';
      const requestBody = typeof body === 'string' ? body : undefined;

      xhr.addEventListener('load', function onXhrLoad() {
        if (!isSearchspringApiUrl(requestUrl)) return;

        const responseType = xhr.responseType;
        if (responseType && responseType !== 'text' && responseType !== '') return;

        let responseText = '';
        try {
          responseText = xhr.responseText || '';
        } catch {
          return;
        }

        ingestSearchspringResponse(requestUrl, requestBody, responseText);
      });

      return origSend.apply(this, arguments);
    };
  }

  function installFetchInterceptor() {
    const originalFetch = window.fetch.bind(window);
    window.fetch = function patchedFetch(input, init) {
      const url =
        typeof input === 'string'
          ? input
          : input instanceof Request
            ? input.url
            : '';

      if (!isSearchspringApiUrl(url)) {
        return originalFetch(input, init);
      }

      const requestBody =
        init?.body && typeof init.body === 'string' ? init.body : undefined;
      const module = inferModuleFromUrl(url, requestBody);

      return originalFetch(input, init).then((response) => {
        if (!response.ok || !module) return response;

        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('json')) return response;

        response
          .clone()
          .text()
          .then((text) => {
            ingestSearchspringResponse(url, requestBody, text);
          })
          .catch(() => {});

        return response;
      });
    };
  }

  function findSideCartConfigScript() {
    return document.querySelector(
      'script[type="searchspring/personalized-recommendations"][profile="side-cart"]'
    );
  }

  function isSideCartRecommendationRoot(root) {
    if (!(root instanceof HTMLElement)) return false;
    if (root.id === SIDE_CART_WIDGET_ID) return true;
    return Boolean(root.closest('#' + SIDE_CART_WIDGET_ID));
  }

  /**
   * @param {HTMLElement} root
   */
  function getRecommendationTag(root) {
    if (isSideCartRecommendationRoot(root)) {
      const recScript = findSideCartConfigScript();
      return (
        recScript?.getAttribute('profile') ||
        recScript?.getAttribute('data-profile') ||
        SIDE_CART_TAG
      );
    }

    const script = root.querySelector('script[type="searchspring/personalized-recommendations"]');
    if (script) {
      return script.getAttribute('profile') || script.getAttribute('data-profile') || '';
    }

    const host = root.querySelector('[searchspring-recommend]') || root.querySelector('motion.div[searchspring-recommend]');
    if (host) {
      return (
        host.getAttribute('profile') ||
        host.getAttribute('data-profile') ||
        host.getAttribute('tag') ||
        ''
      );
    }

    return config.defaultRecommendationProfile || 'similar';
  }

  /**
   * @param {Element} root
   */
  function findResponseIdInElement(root) {
    if (!(root instanceof Element)) return '';

    /** @type {Element[]} */
    const nodes = [
      root,
      ...root.querySelectorAll(
        '[data-response-id], [data-ss-response-id], [response-id], [searchspring-recommend], motion.div[searchspring-recommend]'
      ),
    ];

    for (const node of nodes) {
      for (const attr of ['data-response-id', 'data-ss-response-id', 'response-id', 'responseid']) {
        const value = node.getAttribute(attr);
        if (value) return value.trim();
      }
    }

    return '';
  }

  /**
   * @param {HTMLElement} root
   */
  function getResponseIdFromDom(root) {
    return findResponseIdInElement(root);
  }

  function rememberSideCartResponseId(responseId) {
    if (!responseId) return;

    apiCache.recommendations[SIDE_CART_TAG] = {
      module: 'recommendations',
      tag: SIDE_CART_TAG,
      responseId,
      results: apiCache.recommendations[SIDE_CART_TAG]?.results || [],
    };
  }

  /**
   * @param {HTMLElement} container
   */
  function getImpressionTargets(container) {
    return Array.from(container.querySelectorAll('.ss__result, .swiper-slide'));
  }

  function normalizeProductPath(href) {
    if (!href) return '';
    try {
      return new URL(href, window.location.origin).pathname;
    } catch {
      return href.split('?')[0];
    }
  }

  function getProductFromSideCartWidgetResult(ftfCard) {
    const href =
      ftfCard.getAttribute('data-product-href') ||
      ftfCard.querySelector('a.ftf-card__name')?.getAttribute('href') ||
      ftfCard.querySelector('a[href*="/products/"]')?.getAttribute('href');
    if (!href) return null;

    const targetPath = normalizeProductPath(href);
    const widget = document.getElementById(SIDE_CART_WIDGET_ID);
    if (!widget) return null;

    const results = widget.querySelectorAll('.ss__result');
    for (const result of results) {
      const link = result.querySelector(
        'a.product-thumbnail__title, a.product-thumbnail__link, a[href*="/products/"]'
      );
      const linkHref = link?.getAttribute('href');
      if (!linkHref || normalizeProductPath(linkHref) !== targetPath) continue;

      const product = getProductFromResultElement(result);
      if (product) return product;
    }

    return null;
  }

  /**
   * @param {Element} el
   */
  function getProductFromResultElement(el) {
    const ftfCard = el.closest('.ftf-card');
    if (ftfCard) {
      return getProductFromFtfCard(ftfCard);
    }

    const result = el.closest('.ss__result, .swiper-slide, [data-product-id]');
    if (!result) return null;

    const productId =
      result.getAttribute('data-product-id') ||
      result.querySelector('[data-product-id]')?.getAttribute('data-product-id') ||
      result.querySelector('.swym-button[data-product-id]')?.getAttribute('data-product-id');

    const variantId =
      result.getAttribute('data-variant-id') ||
      result.querySelector('[data-variant-id]')?.getAttribute('data-variant-id') ||
      result.querySelector('.swym-button[data-variant-id]')?.getAttribute('data-variant-id');

    const uid = asId(variantId || productId);
    if (!uid) return null;

    const parentId = asId(productId || uid);
    const sku =
      result.getAttribute('data-sku') ||
      result.querySelector('[data-sku]')?.getAttribute('data-sku') ||
      '';

    const mapped = { type: 'product', parentId, uid };
    if (sku) mapped.sku = sku;
    return mapped;
  }

  /**
   * @param {string} module
   */
  function getDiscoveryCache(module) {
    if (module === 'search') return apiCache.search;
    if (module === 'category') return apiCache.category;
    if (module === 'autocomplete') return apiCache.autocomplete;
    return null;
  }

  /**
   * @param {string} tag
   */
  function getRecommendationCache(tag) {
    return apiCache.recommendations[tag] || apiCache.recommendations.default || null;
  }

  function getSideCartResponseId() {
    const cached = apiCache.recommendations[SIDE_CART_TAG];
    if (cached?.responseId) return cached.responseId;

    const widget = document.getElementById(SIDE_CART_WIDGET_ID);
    if (widget instanceof HTMLElement) {
      const fromDom = findResponseIdInElement(widget);
      if (fromDom) {
        rememberSideCartResponseId(fromDom);
        return fromDom;
      }
    }

    return '';
  }

  function onSideCartResultsReady() {
    const widget = document.getElementById(SIDE_CART_WIDGET_ID);
    if (!(widget instanceof HTMLElement)) {
      scanRecommendationContainers();
      return;
    }

    const fromDom = findResponseIdInElement(widget);
    if (fromDom) rememberSideCartResponseId(fromDom);

    scanRecommendationContainers();

    const dialog = document.querySelector('.cart-drawer__dialog');
    if (
      isDesktopViewport() &&
      dialog?.hasAttribute('open') &&
      widget.querySelector('.ss__result')
    ) {
      onDesktopSideCartDrawerOpen();
    }
  }

  /**
   * Product payload from a desktop Finish The Fit card.
   * @param {Element} card
   */
  function getProductFromFtfCard(card) {
    const productId =
      card.getAttribute('data-product-id') ||
      card.querySelector('[data-product-id]')?.getAttribute('data-product-id');
    const variantId =
      card.getAttribute('data-variant-id') ||
      card.querySelector('[data-variant-id]')?.getAttribute('data-variant-id');

    if (productId || variantId) {
      const uid = asId(variantId || productId);
      if (!uid) return null;
      const parentId = asId(productId || uid);
      const mapped = { type: 'product', parentId, uid };
      const sku = card.getAttribute('data-sku') || '';
      if (sku) mapped.sku = sku;
      return mapped;
    }

    return getProductFromSideCartWidgetResult(card);
  }

  function trackDesktopSideCartRender() {
    const tag = SIDE_CART_TAG;
    if (renderedRecommendationTags.has(tag)) return;

    const responseId = getSideCartResponseId();
    if (!responseId) return;

    renderedRecommendationTags.add(tag);
    track('recommendations', 'render', { tag, responseId });
  }

  /**
   * Fire impression for all .ftf-card elements when the minicart opens (desktop).
   * @param {number} [attempt]
   */
  function trackDesktopSideCartImpressions(attempt = 0) {
    if (!isDesktopViewport()) return;

    const panel = document.getElementById('finish-the-fit-panel');
    const cards = panel ? panel.querySelectorAll('.ftf-card') : [];
    const responseId = getSideCartResponseId();
    if ((!responseId || !cards.length) && attempt < 10) {
      window.setTimeout(() => trackDesktopSideCartImpressions(attempt + 1), 200);
      return;
    }

    if (!responseId || !cards.length) return;

    trackDesktopSideCartRender();

    /** @type {any[]} */
    const results = [];

    cards.forEach((card) => {
      const product = getProductFromFtfCard(card);
      if (product) results.push(product);
    });

    if (!results.length) return;

    track('recommendations', 'impression', {
      tag: SIDE_CART_TAG,
      responseId,
      results,
      banners: [],
    });
  }

  /** @type {number | undefined} */
  let desktopSideCartOpenTimer;

  function onDesktopSideCartDrawerOpen() {
    if (!isDesktopViewport()) return;
    window.clearTimeout(desktopSideCartOpenTimer);
    desktopSideCartOpenTimer = window.setTimeout(() => trackDesktopSideCartImpressions(0), 100);
  }

  function bindDesktopSideCartBeacon() {
    document.addEventListener(
      'click',
      (e) => {
        if (!isDesktopViewport()) return;

        const target = e.target;
        if (!(target instanceof Element)) return;

        if (target.closest('.ss-size-wrap, .ss-atc-btn, .ftf-atc-btn, .swym-button')) {
          return;
        }

        const card = target.closest('#finish-the-fit-panel .ftf-card');
        if (!card) return;

        const responseId = getSideCartResponseId();
        const product = getProductFromFtfCard(card);
        if (!responseId || !product) return;

        track('recommendations', 'clickthrough', {
          tag: SIDE_CART_TAG,
          responseId,
          results: [product],
          banners: [],
        });
      },
      true
    );

    function bindCartDrawerOpenListeners() {
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer instanceof HTMLElement && drawer.dataset.ssBeaconCartOpen !== '1') {
        drawer.dataset.ssBeaconCartOpen = '1';
        // dialog:open is dispatched on cart-drawer-component and does not bubble.
        drawer.addEventListener('dialog:open', onDesktopSideCartDrawerOpen);
      }

      const dialog = document.querySelector('.cart-drawer__dialog');
      if (dialog instanceof HTMLElement && dialog.dataset.ssBeaconCartOpen !== '1') {
        dialog.dataset.ssBeaconCartOpen = '1';
        new MutationObserver(() => {
          if (!dialog.hasAttribute('open')) return;
          onDesktopSideCartDrawerOpen();
        }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
      }
    }

    bindCartDrawerOpenListeners();
    // Beacon loads in <head>; cart-drawer is in the body — bind when it appears.
    new MutationObserver(bindCartDrawerOpenListeners).observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * @param {string} module
   * @param {string} event
   * @param {Record<string, unknown>} data
   */
  function track(module, event, data) {
    send(module, event, data);
  }

  function trackProductPageview() {
    const product = config.product;
    if (!product) return;

    const uid = asId(product.variantId || product.productId);
    const parentId = asId(product.productId || uid);
    if (!uid) return;

    const result = { parentId, uid };
    const sku = asId(product.sku);
    if (sku) result.sku = sku;

    track('product', 'pageview', { result });
  }

  function trackShopperLogin() {
    const shopperId = (config.shopperId || '').trim();
    if (!shopperId) return;

    const sentFor = readStorage(STORAGE_LOGIN_SENT);
    if (sentFor === shopperId) return;

    send('shopper', 'login');
    writeStorage(STORAGE_LOGIN_SENT, shopperId);
  }

  function trackOrderTransaction() {
    const order = config.order;
    if (!order || !order.orderId) return;

    const results = (order.items || [])
      .map((/** @type {any} */ item) => {
        const line = mapCartLine(
          {
            product_id: item.productId,
            variant_id: item.variantId,
            sku: item.sku,
            quantity: item.quantity,
            price: item.price,
          },
          item.quantity,
          item.price
        );
        return line;
      })
      .filter(Boolean);

    track('order', 'transaction', {
      orderId: String(order.orderId),
      transactionTotal: Number(order.transactionTotal || order.total || 0),
      total: Number(order.total || 0),
      results,
      ...(order.city ? { city: order.city } : {}),
      ...(order.state ? { state: order.state } : {}),
      ...(order.country ? { country: order.country } : {}),
      ...(order.vat != null ? { vat: order.vat } : {}),
    });
  }

  /**
   * @param {any[]} cartItems
   * @param {any[]} changedItems
   * @param {'add' | 'remove'} type
   */
  function trackCartDelta(cartItems, changedItems, type) {
    const cart = mapCartState(cartItems);
    const results = changedItems.map((item) => mapCartLine(item)).filter(Boolean);
    if (!results.length) return;

    track('cart', type, { results, cart });
  }

  async function refreshCartAndTrack() {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) return;
      const cart = await res.json();
      const items = Array.isArray(cart.items) ? cart.items : [];

      if (lastCartSnapshot) {
        /** @type {Map<string, any>} */
        const prev = new Map(
          lastCartSnapshot.map((/** @type {any} */ i) => [String(i.variant_id), i])
        );
        /** @type {Map<string, any>} */
        const next = new Map(items.map((/** @type {any} */ i) => [String(i.variant_id), i]));

        /** @type {any[]} */
        const added = [];
        /** @type {any[]} */
        const removed = [];

        next.forEach((item, vid) => {
          const before = prev.get(vid);
          if (!before) {
            added.push(item);
          } else if (item.quantity > before.quantity) {
            added.push({ ...item, quantity: item.quantity - before.quantity });
          }
        });

        prev.forEach((item, vid) => {
          const after = next.get(vid);
          if (!after) {
            removed.push(item);
          } else if (after.quantity < item.quantity) {
            removed.push({ ...item, quantity: item.quantity - after.quantity });
          }
        });

        if (added.length) trackCartDelta(items, added, 'add');
        if (removed.length) trackCartDelta(items, removed, 'remove');
      }

      lastCartSnapshot = items;
    } catch {
      /* ignore */
    }
  }

  /**
   * @param {HTMLElement} container
   */
  function trackRecommendationsRender(container) {
    const tag = getRecommendationTag(container);
    if (!tag || renderedRecommendationTags.has(tag)) return;

    const cache = getRecommendationCache(tag);
    const responseId = cache?.responseId || getResponseIdFromDom(container);
    if (!responseId) return;

    renderedRecommendationTags.add(tag);
    track('recommendations', 'render', { tag, responseId });

    if (tag === SIDE_CART_TAG && !isDesktopViewport()) {
      bindRecommendationsImpressions(container, tag, responseId);
    } else if (tag !== SIDE_CART_TAG) {
      bindRecommendationsImpressions(container, tag, responseId);
    }
  }

  /**
   * @param {HTMLElement} container
   * @param {string} tag
   * @param {string} responseId
   */
  function bindRecommendationsImpressions(container, tag, responseId) {
    const key = `${tag}:${responseId}`;
    if (!impressedKeys.has(key)) impressedKeys.set(key, new Set());
    const seen = impressedKeys.get(key);

    let observer = impressionObservers.get(container);
    if (!observer) {
      observer = new IntersectionObserver(
        (entries) => {
          /** @type {any[]} */
          const batch = [];

          entries.forEach((entry) => {
            if (!entry.isIntersecting || entry.intersectionRatio < IMPRESSION_THRESHOLD) return;
            const product = getProductFromResultElement(/** @type {Element} */ (entry.target));
            if (!product) return;

            const dedupeKey = `${product.uid}:${product.parentId}`;
            if (seen?.has(dedupeKey)) return;
            seen?.add(dedupeKey);
            batch.push(product);
          });

          if (!batch.length) return;

          track('recommendations', 'impression', {
            tag,
            responseId,
            results: batch,
            banners: [],
          });
        },
        { threshold: [IMPRESSION_THRESHOLD] }
      );
      impressionObservers.set(container, observer);
    }

    getImpressionTargets(container).forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      if (el.dataset.beaconImpressionObserved === '1') return;
      el.dataset.beaconImpressionObserved = '1';
      observer.observe(el);
    });
  }

  /**
   * @param {HTMLElement} container
   */
  function scanRecommendationContainers() {
    document.querySelectorAll(RECOMMENDATION_CONTAINER_SELECTOR).forEach((node) => {
      if (node instanceof HTMLElement) trackRecommendationsRender(node);
    });
  }

  /**
   * @param {Element} target
   * @param {'clickthrough' | 'addtocart'} event
   * @param {{ qty?: number, price?: number }} [extra]
   */
  function trackFromDomContext(target, event, extra) {
    const recRoot = target.closest(RECOMMENDATION_ROOT_SELECTOR);
    if (recRoot instanceof HTMLElement) {
      const tag = getRecommendationTag(recRoot);
      const responseId =
        getRecommendationCache(tag)?.responseId || getResponseIdFromDom(recRoot);
      const product = getProductFromResultElement(target);
      if (!tag || !responseId || !product) return;

      /** @type {Record<string, unknown>} */
      const result = { ...product };
      if (event === 'addtocart') {
        result.qty = extra?.qty ?? 1;
        if (extra?.price != null) result.price = extra.price;
      }

      track('recommendations', event, {
        tag,
        responseId,
        results: [result],
        banners: [],
      });
      return true;
    }

    const plpRoot = target.closest('#plp-app, .search-app');
    if (plpRoot) {
      const page = document.querySelector('#plp-app')?.getAttribute('data-page') || '';
      const module = page === 'search_page' ? 'search' : 'category';
      const cache = getDiscoveryCache(module);
      const product = getProductFromResultElement(target);
      if (!cache?.responseId || !product) return false;

      /** @type {Record<string, unknown>} */
      const result = { ...product };
      if (event === 'addtocart') {
        result.qty = extra?.qty ?? 1;
        if (extra?.price != null) result.price = extra.price;
      }

      track(module, event, {
        responseId: cache.responseId,
        results: [result],
        banners: [],
      });
      return true;
    }

    return false;
  }

  function bindGlobalClickthrough() {
    document.addEventListener(
      'click',
      (e) => {
        const target = e.target;
        if (!(target instanceof Element)) return;

        const link = target.closest(
          'a.product-thumbnail__title, a.product-thumbnail__link, .ss__result a[href*="/products/"]'
        );
        if (!link) return;

        trackFromDomContext(link, 'clickthrough');
      },
      true
    );
  }

  // Allow search-app (and integrations) to call explicitly
  /** @type {any} */
  window.SearchspringBeacon = {
    track,
    send,
    trackProductPageview,
    trackShopperLogin,
    trackOrderTransaction,
    trackCartDelta,
    /**
     * @param {string} tag
     * @param {string} responseId
     * @param {any[]} [results]
     */
    setRecommendationResponse(tag, responseId, results) {
      apiCache.recommendations[tag] = {
        module: 'recommendations',
        tag,
        responseId,
        results: results || [],
      };
      scanRecommendationContainers();
    },
    /**
     * @param {string} module
     * @param {string} responseId
     * @param {any[]} [results]
     */
    setDiscoveryResponse(module, responseId, results) {
      if (module === 'search') apiCache.search = { module, responseId, results: results || [] };
      if (module === 'category') apiCache.category = { module, responseId, results: results || [] };
      if (module === 'autocomplete') {
        apiCache.autocomplete = { module, responseId, results: results || [] };
      }
    },
    /**
     * @param {Element} target
     * @param {{ qty?: number, price?: number }} [extra]
     */
    trackAddToCartFromElement(target, extra) {
      return trackFromDomContext(target, 'addtocart', extra);
    },
    trackRedirect(module, responseId, redirectUrl) {
      if (!responseId || !redirectUrl) return;
      track(module, 'redirect', { responseId, redirect: redirectUrl });
    },
    refreshPageLoadId() {
      pageLoadId = uuid();
    },
  };

  installFetchInterceptor();
  installXhrInterceptor();
  bindGlobalClickthrough();
  bindDesktopSideCartBeacon();

  document.addEventListener('cart:update', () => {
    refreshCartAndTrack();
  });

  window.addEventListener('ss-on-results-load', onSideCartResultsReady);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      trackProductPageview();
      trackShopperLogin();
      trackOrderTransaction();
      refreshCartAndTrack();
      scanRecommendationContainers();
    });
  } else {
    trackProductPageview();
    trackShopperLogin();
    trackOrderTransaction();
    refreshCartAndTrack();
    scanRecommendationContainers();
  }

  const recObserver = new MutationObserver(() => {
    scanRecommendationContainers();
  });
  recObserver.observe(document.documentElement, { childList: true, subtree: true });
})();
